<template>
  <div class="cf-flex cf-flex--column cf-flex--gap-16 cf-padding-y-16">
    <Teleport
      defer
      to="#portal-modal-header-target"
    >
      Store Details
    </Teleport>

    <div class="cf-flex cf-flex--column cf-flex--align-center cf-flex--gap-8">
      <h2 class="cf-title cf-title--1">
        {{ data.location.displayName || data.location.description }}
      </h2>

      <p class="cf-subtitle cf-subtitle--2 cf-subtitle--green cf-subtitle--heavy">
        {{ storeOperatingStatus }}
      </p>
    </div>

    <template v-if="data.location.address || data.location.phoneNumber">
      <div class="cf-flex cf-flex--column cf-flex--align-center cf-flex--gap-8">
        <cf-store-directions
          :address="data.location.address"
          :element-type="'link'"
          show-landmark-or-cross-street
        />

        <cf-store-phone-number
          v-if="data.location.phoneNumber"
          :phone-number="data.location.phoneNumber"
          :element-type="'link'"
        />
      </div>

      <hr class="cf-hr">
    </template>

    <p v-if="data.extraInfo">
      {{ data.extraInfo }}
    </p>

    <div
      v-if="!data.hideOrderingModes && orderDetails.orderType !== 'group' && orderDetails.orderType !== 'dine-in' && orderDetails.orderType !== 'table'"
      class="cf-flex cf-flex--column cf-flex--gap-18"
    >
      <!-- Ordering Modes Container -->
      <template v-if="locationHasOrderingModes">
        <div class="cf-flex cf-flex--column cf-flex--gap-8 cf-text-align-left">
          <h3 class="cf-title cf-title--3">
            Ordering Modes
          </h3>

          <div class="cf-flex cf-flex--row cf-flex--gap-12 cf-flex--wrap">
            <button
              v-if="isTakeoutOrderingModeEnabled"
              type="button"
              class="cf-button cf-button--tertiary cf-button--sm"
              @click="standardMenuClickHandler('pick-up')"
            >
              <span class="cf-icon cf-icon--black far fa-shopping-bag" />
              {{ content.OrderModes.metadata.menuTypeLabelPickup }}
            </button>

            <button
              v-if="isDeliveryOrderingModeEnabled"
              type="button"
              class="cf-button cf-button--tertiary cf-button--sm"
              @click="standardMenuClickHandler('delivery')"
            >
              <span class="cf-icon cf-icon--black far fa-car" />
              {{ content.OrderModes.metadata.menuTypeLabelDelivery }}
            </button>

            <button
              v-if="isRoomServiceOrderingModeEnabled"
              type="button"
              class="cf-button cf-button--tertiary cf-button--sm"
              @click="standardMenuClickHandler('room-service')"
            >
              <span class="cf-icon cf-icon--black far fa-door-closed" />
              {{ content.OrderModes.metadata.menuTypeLabelRoomService }}
            </button>

            <button
              v-if="isShippingOrderingModeEnabled"
              type="button"
              class="cf-button cf-button--tertiary cf-button--sm"
              @click="standardMenuClickHandler('shipping')"
            >
              <span class="cf-icon cf-icon--black far fa-truck" />
              {{ content.OrderModes.metadata.menuTypeLabelShipping }}
            </button>

            <button
              v-if="showCateringMenuButton"
              type="button"
              class="cf-button cf-button--tertiary cf-button--sm"
              @click="cateringMenuClickHandler"
            >
              <span class="cf-icon cf-icon--black far fa-hat-chef" />
              Catering
            </button>
          </div>
        </div>

        <hr class="cf-hr">
      </template>

      <!-- Pickup Methods Container -->
      <template v-if="availablePickupMethods.length">
        <div class="cf-flex cf-flex--column cf-flex--gap-8 cf-text-align-left">
          <h3 class="cf-title cf-title--3">
            Available Pickup Locations
          </h3>

          <div class="cf-flex cf-flex--row cf-flex--gap-12">
            <p
              v-for="(pickupMethod, idx) in availablePickupMethods"
              :key="idx"
              class="cf-subtitle cf-subtitle--2 bold"
            >
              {{ pickupMethod.description }}
            </p>
          </div>
        </div>

        <hr class="cf-hr">
      </template>
    </div>

    <div
      v-if="data.location.hasStoreHours"
      class="cf-flex cf-flex--column cf-flex--gap-8 cf-text-align-left"
    >
      <h3 class="cf-title cf-title--3">
        Store Hours
      </h3>

      <ul class="cf-highlight-list">
        <li
          v-for="(day, dayInWeek) in data.location.dailyOperatingSchedules"
          :key="dayInWeek"
          :class="['cf-highlight-list__item', { 'cf-highlight-list__item--active': day.isCurrentDay }]"
        >
          <div class="cf-flex-grow-1">
            <p :class="['cf-subtitle cf-subtitle--2', {'cf-subtitle--black cf-subtitle--heavy': day.isCurrentDay }]">
              {{ dayInWeek }}
            </p>
          </div>

          <div class="cf-flex cf-flex--row cf-flex--gap-8 cf-text-align-right">
            <p
              v-if="day.isCurrentDay && day.status === 'Closed'"
              class="cf-subtitle cf-subtitle--2 cf-subtitle--red"
            >
              (Closed)
            </p>

            <div
              v-if="day.isOpen"
              class="cf-flex cf-flex--column cf-flex--gap-6 cf-text-align-right"
            >
              <p
                v-for="schedule in day.openCloseItems"
                :key="schedule.open"
                :class="['cf-subtitle cf-subtitle--2', {'cf-subtitle--black cf-subtitle--heavy': day.isCurrentDay }]"
              >
                {{ $timeOfDay(schedule.open) }} – {{ $timeOfDay(schedule.close) }}
              </p>
            </div>

            <p
              v-else
              class="cf-subtitle cf-subtitle--2"
            >
              Closed
            </p>
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>


<script>
  import { mapGetters } from 'vuex';

  import orderModeNavigationMixin from '@/mixins/order-mode-navigation';

  import CfStoreDirections from '@/view/components/shared/cf-store-directions/index.vue';
  import CfStorePhoneNumber from '@/view/components/shared/cf-store-phone-number/index.vue';


  export default {
    name: 'LocationDetails',

    components: {
      CfStoreDirections,
      CfStorePhoneNumber
    },

    mixins: [orderModeNavigationMixin],

    props: {
      data: {
        type: Object,
        required: true
      }
    },

    computed: {
      ...mapGetters('content', ['content']),
      ...mapGetters('merchant', ['merchant']),
      ...mapGetters('session', ['isPortalAuthSession', 'orderDetails']),
      ...mapGetters('modals', ['currentlyOpenModal']),
      ...mapGetters('locations', ['selectedLocation']),
      ...mapGetters('orders', ['order', 'showCateringMenuButton']),

      locationHasOrderingModes() {
        return this.isTakeoutOrderingModeEnabled
          || this.isDeliveryOrderingModeEnabled
          || this.isRoomServiceOrderingModeEnabled
          || this.isShippingOrderingModeEnabled
          || this.showCateringMenuButton;
      },

      isTakeoutOrderingModeEnabled() {
        return this.data.location.features.TakeOut && !this.isPortalAuthSession;
      },

      isDeliveryOrderingModeEnabled() {
        return this.data.location.features.Delivery && !this.isPortalAuthSession;
      },

      isRoomServiceOrderingModeEnabled() {
        return this.data.location.features.RoomService && !this.isPortalAuthSession;
      },

      isShippingOrderingModeEnabled() {
        return this.data.location.features.Shipping && !this.isPortalAuthSession;
      },

      storeOperatingStatus() {
        return this.data.location.currentDayOperatingSchedule.status === 'Online' ? 'Open Now' : 'Closed';
      },

      availablePickupMethods() {
        return this.selectedLocation.pickupFulfillmentTypes.reduce((curr, next) => {
          if (next.value === 'curbside') curr.push({ description: this.content.OrderSummary?.metadata.curbsideSelection || 'Curbside', icon: 'fa-parking' });

          if (next.value === 'take-out') curr.push({ description: this.content.OrderSummary?.metadata.insideSelection || 'Inside', icon: 'fa-store-alt' });

          if (next.value === 'drive-through') curr.push({ description: this.content.OrderSummary?.metadata.driveThroughSelection, icon: 'fa-car-building' });

          return curr;
        }, []);
      }
    },

    watch: {
      currentlyOpenModal: 'handleOpenModalChange'
    },

    methods: {
      handleOpenModalChange(modal) {
        if (modal && modal.name === 'locationDetails') {
          // this.$triggerGoogleTrackingEvent('ViewStoreInfo', 'Locations');

          this.$triggerNewRelicEvent('ViewStoreInfo', 'Information', 'View Store Info');
        }
      }
    }
  };
</script>
